import { auth, db, functions } from "@infrastructure/config/firebase";
import type {
  AuthService,
  AuthUser,
  InitiatePhoneAuthResult,
  PhoneConfirmationResult,
  RecaptchaVerifierInstance,
  UserCredential,
} from "@infrastructure/interfaces/database.interface";
import logger from "@logger";
import {
  type ConfirmationResult,
  type UserCredential as FirebaseUserCredential,
  onAuthStateChanged,
  RecaptchaVerifier,
  signInWithCustomToken,
  signInWithPhoneNumber,
  signOut,
} from "firebase/auth";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

/**
 * Wraps Firebase's ConfirmationResult to match our abstract interface.
 */
class FirebasePhoneConfirmationResult implements PhoneConfirmationResult {
  private readonly firebaseResult: ConfirmationResult;
  private readonly phoneNumber: string;

  constructor(firebaseResult: ConfirmationResult, phoneNumber: string) {
    this.firebaseResult = firebaseResult;
    this.phoneNumber = phoneNumber;
  }

  get verificationId(): string {
    return this.firebaseResult.verificationId;
  }

  async confirm(verificationCode: string): Promise<UserCredential> {
    try {
      const result = await this.firebaseResult.confirm(verificationCode);
      return mapFirebaseCredential(result);
    } catch (error) {
      const bypassResult = await tryFixtureBypassLogin(this.phoneNumber, verificationCode, error);
      return mapFirebaseCredential(bypassResult);
    }
  }
}

function mapFirebaseCredential(result: FirebaseUserCredential): UserCredential {
  return {
    user: {
      uid: result.user.uid,
      email: result.user.email,
      phoneNumber: result.user.phoneNumber,
      displayName: result.user.displayName,
      photoURL: result.user.photoURL,
    },
  };
}

function isFixtureBypassEnabled(): boolean {
  return import.meta.env.VITE_ENABLE_FIXTURE_LOGIN_BYPASS === "true";
}

function isFixturePhone(phoneNumber: string): boolean {
  return phoneNumber.startsWith("+447922");
}

async function tryFixtureBypassLogin(
  phoneNumber: string,
  bypassCode: string,
  originalError: unknown
): Promise<FirebaseUserCredential> {
  if (!isFixtureBypassEnabled() || !isFixturePhone(phoneNumber)) {
    throw originalError;
  }
  try {
    const callable = httpsCallable<{ phoneNumber: string; bypassCode: string }, { customToken: string }>(
      functions,
      "createFixtureLoginCustomToken"
    );
    const response = await callable({ phoneNumber, bypassCode });
    const token = response.data.customToken;
    if (typeof token !== "string" || token.length === 0) {
      throw originalError;
    }
    return await signInWithCustomToken(auth, token);
  } catch (bypassError) {
    const error = new Error("Fixture bypass sign-in failed");
    Object.assign(error, {
      code: "auth/fixture-bypass-failed",
      cause: bypassError,
    });
    throw error;
  }
}

/**
 * Wraps Firebase's RecaptchaVerifier to match our abstract interface.
 */
class FirebaseRecaptchaVerifier implements RecaptchaVerifierInstance {
  private verifier: RecaptchaVerifier;

  constructor(containerId: string, invisible: boolean = true) {
    this.verifier = new RecaptchaVerifier(auth, containerId, {
      size: invisible ? "invisible" : "normal",
    });
  }

  async render(): Promise<number> {
    return await this.verifier.render();
  }

  clear(): void {
    this.verifier.clear();
  }

  getFirebaseVerifier(): RecaptchaVerifier {
    return this.verifier;
  }
}

/**
 * Firebase implementation of phone-based authentication service.
 * Uses SMS verification codes for passwordless authentication.
 */
export class FirebaseAuthService implements AuthService {
  private async ensureUserDocument(user: AuthUser): Promise<void> {
    const userDocRef = doc(db, "users", user.uid);
    const userDoc = await getDoc(userDocRef);

    if (!userDoc.exists()) {
      await setDoc(userDocRef, {
        uid: user.uid,
        phone_number: user.phoneNumber,
        created_time: serverTimestamp(),
        liked_uids: [],
        chat_partner_uids: [],
        rejected_uids: [],
        blockedUsers: [],
        unlockedEventIds: [],
      });
      logger.debug("Created user document for authenticated user:", user.uid);
    }
  }

  /**
   * Sends an SMS verification code to the provided phone number.
   */
  async sendVerificationCode(
    phoneNumber: string,
    recaptchaVerifier: RecaptchaVerifierInstance
  ): Promise<PhoneConfirmationResult> {
    try {
      const firebaseVerifier = (recaptchaVerifier as FirebaseRecaptchaVerifier).getFirebaseVerifier();
      const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, firebaseVerifier);
      return new FirebasePhoneConfirmationResult(confirmationResult, phoneNumber);
    } catch (error) {
      logger.error("Error sending verification code:", error);
      throw error;
    }
  }

  /**
   * Confirms the SMS verification code and completes authentication.
   * Creates a new user document if this is a new user.
   */
  async confirmVerificationCode(
    confirmationResult: PhoneConfirmationResult,
    verificationCode: string
  ): Promise<UserCredential> {
    try {
      const userCredential = await confirmationResult.confirm(verificationCode);
      const user = userCredential.user;

      if (user) {
        await this.ensureUserDocument(user);
      }

      return userCredential;
    } catch (error) {
      logger.error("Error confirming verification code:", error);
      throw error;
    }
  }

  async signOut(): Promise<void> {
    try {
      await signOut(auth);
    } catch (error) {
      logger.error("Error signing out:", error);
      throw error;
    }
  }

  getCurrentAuthUser(): AuthUser | null {
    const user = auth.currentUser;
    if (!user) {
      return null;
    }

    return {
      uid: user.uid,
      email: user.email,
      phoneNumber: user.phoneNumber,
      displayName: user.displayName,
      photoURL: user.photoURL,
    };
  }

  onAuthStateChanged(callback: (user: AuthUser | null) => void): () => void {
    return onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const authUser: AuthUser = {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          phoneNumber: firebaseUser.phoneNumber,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
        };

        try {
          await this.ensureUserDocument(authUser);
        } catch (error) {
          logger.error("Failed to ensure user document on auth state change:", error);
        }

        callback(authUser);
      } else {
        callback(null);
      }
    });
  }

  /**
   * Creates a reCAPTCHA verifier for phone authentication.
   */
  createRecaptchaVerifier(containerId: string, invisible: boolean = true): RecaptchaVerifierInstance {
    return new FirebaseRecaptchaVerifier(containerId, invisible);
  }

  /**
   * Checks whether a phone number belongs to a new or returning user.
   * New users receive a custom token for instant sign-in (no OTP).
   */
  async initiatePhoneAuth(phoneNumber: string): Promise<InitiatePhoneAuthResult> {
    try {
      const callable = httpsCallable<{ phoneNumber: string }, InitiatePhoneAuthResult>(functions, "initiatePhoneAuth");
      const response = await callable({ phoneNumber });
      return response.data;
    } catch (error) {
      logger.error("Error initiating phone auth:", error);
      throw error;
    }
  }

  /**
   * Signs in with a server-issued custom token (used for new-user instant sign-in).
   */
  async signInWithCustomToken(token: string): Promise<void> {
    try {
      await signInWithCustomToken(auth, token);
    } catch (error) {
      logger.error("Error signing in with custom token:", error);
      throw error;
    }
  }
}

export const firebaseAuthService = new FirebaseAuthService();
export default firebaseAuthService;
